import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { BookOpenCheck, PlusCircle, Trash2, History as HistoryIcon, ShieldOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function Playbooks() {
  const { activeWorkspaceId, can } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", scope: "company", description: "", rules: [""], exclusions: [], auto_include_exclusions: true });
  const [historyView, setHistoryView] = useState(null);

  const seedStandard = async () => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/playbooks/standard-exclusions`);
      toast.success(data.created ? "Standard Exclusions playbook created" : "Standard Exclusions refreshed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get(`/workspaces/${activeWorkspaceId}/playbooks`);
    setItems(data);
  }, [activeWorkspaceId]);
  useEffect(() => { if (activeWorkspaceId) load(); }, [activeWorkspaceId, load]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", scope: "company", description: "", rules: [""], exclusions: [], auto_include_exclusions: true });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, scope: p.scope, description: p.description || "", rules: p.rules?.length ? p.rules : [""], exclusions: p.exclusions || [], auto_include_exclusions: p.auto_include_exclusions !== false, auto_run_on_new_project: !!p.auto_run_on_new_project });
    setOpen(true);
  };

  const submit = async () => {
    const payload = { ...form, rules: form.rules.filter((r) => r.trim()), exclusions: form.exclusions.filter((r) => r.trim()) };
    try {
      if (editing) await api.patch(`/playbooks/${editing.id}`, payload);
      else await api.post(`/workspaces/${activeWorkspaceId}/playbooks`, payload);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this playbook?")) return;
    await api.delete(`/playbooks/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="playbooks-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-cap text-amber-500">// REVIEW PLAYBOOKS</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-100 mt-1">Custom Review Playbooks</h1>
          <p className="text-slate-400 text-sm mt-1">Natural-language rules combined with AI scans. Versioned and combinable.</p>
        </div>
        {can("create") && (
          <div className="flex gap-2">
            <button onClick={seedStandard} className="border border-slate-700 hover:border-amber-500 text-slate-100 px-4 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="seed-standard-exclusions-button">
              <ShieldOff className="w-4 h-4" /> Standard exclusions
            </button>
            <button onClick={openNew} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="new-playbook-button">
              <PlusCircle className="w-4 h-4" /> New playbook
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="sc-card p-5" data-testid={`playbook-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <BookOpenCheck className="w-4 h-4 text-amber-400" />
                  <span className="font-heading font-semibold text-slate-100">{p.name}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs">
                  <span className="px-1.5 py-0.5 rounded font-mono uppercase bg-slate-800 text-slate-300">{p.scope}</span>
                  <span className="font-mono text-slate-500">v{p.version}</span>
                </div>
              </div>
              <button onClick={() => setHistoryView(p)} className="text-slate-500 hover:text-slate-200" data-testid={`playbook-history-${p.id}`}>
                <HistoryIcon className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-3">{p.description || "—"}</p>
            <div className="mt-3">
              <div className="label-cap mb-1">Rules ({p.rules?.length || 0})</div>
              <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
                {p.rules?.slice(0, 4).map((r) => (
                  <li key={r} className="text-slate-300 border-l border-amber-500/40 pl-2">{r}</li>
                ))}
                {(p.rules?.length || 0) > 4 && <li className="text-slate-500 italic">+{p.rules.length - 4} more…</li>}
              </ul>
              {(p.exclusions?.length || 0) > 0 && (
                <div className="mt-3" data-testid={`playbook-exclusions-${p.id}`}>
                  <div className="label-cap mb-1">Exclusions ({p.exclusions.length}) {p.auto_include_exclusions !== false && <span className="text-emerald-400">· in PM report</span>}</div>
                  <ul className="text-xs space-y-1 max-h-24 overflow-y-auto">
                    {p.exclusions.slice(0, 3).map((r) => <li key={r} className="text-slate-400 border-l border-slate-600 pl-2 truncate">{r}</li>)}
                    {p.exclusions.length > 3 && <li className="text-slate-500 italic">+{p.exclusions.length - 3} more…</li>}
                  </ul>
                </div>
              )}
            </div>
            {can("edit") && (
              <div className="mt-4 flex justify-between">
                <button onClick={() => openEdit(p)} className="text-xs text-amber-400 hover:text-amber-300" data-testid={`playbook-edit-${p.id}`}>Edit</button>
                {can("delete") && <button onClick={() => remove(p.id)} className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1" data-testid={`playbook-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
              </div>
            )}
          </div>
        ))}
        {!items.length && (
          <div className="col-span-full sc-card p-10 text-center text-slate-500">
            <BookOpenCheck className="w-10 h-10 mx-auto mb-3 text-slate-700" />
            No playbooks yet. Create one to encode your company's steel review policies.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit playbook" : "New playbook"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="label-cap">Name</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-slate-100 focus:outline-none focus:border-amber-500"
                data-testid="playbook-name-input" />
            </label>
            <label>
              <span className="label-cap">Scope</span>
              <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
                className="mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-slate-100"
                data-testid="playbook-scope-select">
                <option value="company">Company</option>
                <option value="project">Project</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="label-cap">Description</span>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-slate-100 focus:outline-none focus:border-amber-500" />
          </label>
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="label-cap">Rules (natural language)</span>
              <button onClick={() => setForm({ ...form, rules: [...form.rules, ""] })} className="text-xs text-amber-400 hover:text-amber-300" data-testid="playbook-add-rule-button">+ Add rule</button>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {form.rules.map((r, i) => (
                <div key={`rule-${i}-${form.rules.length}`} className="flex gap-2">
                  <textarea value={r} rows={2} placeholder="e.g. Flag any spec requiring fabricator to pay for 3rd party CWI inspections"
                    onChange={(e) => { const arr = [...form.rules]; arr[i] = e.target.value; setForm({ ...form, rules: arr }); }}
                    className="flex-1 bg-slate-950/60 border border-slate-800 rounded-md px-2 py-1.5 text-slate-100 font-mono text-xs focus:outline-none focus:border-amber-500"
                    data-testid={`playbook-rule-input-${i}`} />
                  <button onClick={() => setForm({ ...form, rules: form.rules.filter((_, x) => x !== i) })} className="text-red-400 hover:text-red-300 text-xs">✕</button>
                </div>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-300" data-testid="playbook-auto-run-toggle-label">
            <input type="checkbox" checked={!!form.auto_run_on_new_project} onChange={(e) => setForm({ ...form, auto_run_on_new_project: e.target.checked })} data-testid="playbook-auto-run-toggle" />
            Automatically run on every new project (company playbooks)
          </label>
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="label-cap">Standard exclusions (auto-listed in PM report)</span>
              <div className="flex items-center gap-3">
                <label className="text-xs text-slate-400 inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={form.auto_include_exclusions} onChange={(e) => setForm({ ...form, auto_include_exclusions: e.target.checked })} data-testid="playbook-auto-exclusions-toggle" /> include in reports
                </label>
                <button onClick={() => setForm({ ...form, exclusions: [...form.exclusions, ""] })} className="text-xs text-amber-400 hover:text-amber-300" data-testid="playbook-add-exclusion-button">+ Add exclusion</button>
              </div>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {form.exclusions.map((r, i) => (
                <div key={`excl-${i}-${form.exclusions.length}`} className="flex gap-2">
                  <input value={r} placeholder="e.g. Erection of structural steel is excluded (furnish only)."
                    onChange={(e) => { const arr = [...form.exclusions]; arr[i] = e.target.value; setForm({ ...form, exclusions: arr }); }}
                    className="flex-1 bg-slate-950/60 border border-slate-800 rounded-md px-2 py-1.5 text-slate-100 font-mono text-xs focus:outline-none focus:border-amber-500"
                    data-testid={`playbook-exclusion-input-${i}`} />
                  <button onClick={() => setForm({ ...form, exclusions: form.exclusions.filter((_, x) => x !== i) })} className="text-red-400 hover:text-red-300 text-xs">✕</button>
                </div>
              ))}
              {!form.exclusions.length && <div className="text-xs text-slate-500">No exclusions on this playbook.</div>}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200">Cancel</button>
            <button onClick={submit} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-md text-sm" data-testid="playbook-save-button">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyView} onOpenChange={(o) => !o && setHistoryView(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-xl">
          <DialogHeader><DialogTitle className="font-heading">Version history — {historyView?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {historyView?.history?.length ? historyView.history.slice().reverse().map((h, i) => (
              <div key={h.saved_at || i} className="border border-slate-800 rounded-md p-3 text-xs">
                <div className="flex justify-between font-mono text-slate-500 mb-1">
                  <span>v{h.version}</span>
                  <span>{new Date(h.saved_at).toLocaleString()}</span>
                </div>
                <div className="font-semibold text-slate-100">{h.name}</div>
                <ul className="mt-1 space-y-0.5">
                  {h.rules?.map((r, j) => <li key={j} className="text-slate-400 border-l border-slate-700 pl-2">{r}</li>)}
                </ul>
              </div>
            )) : <div className="text-slate-500 text-sm">No previous versions.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
